import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { IconRecordAccount, IconRecordContact, IconRecordLead, IconRecordOpportunity } from '../../../base/icons/svg';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { RecordListItem } from './RecordListItem';
const useStyles = makeStyles()(theme => {
    return {
        section: {
            marginBottom: '16px'
        },
        groupTitle: {
            fontSize: '0.75rem',
            fontWeight: 600,
            color: theme.palette.text03,
            textTransform: 'uppercase',
            margin: '16px 0 8px 0'
        },
        list: {
            listStyle: 'none',
            margin: 0,
            padding: 0
        },
        noResults: {
            textAlign: 'center',
            color: theme.palette.text03,
            padding: '24px'
        }
    };
});
/**
 * Component for displaying Salesforce search results with link buttons.
 *
 * @param {IProps} props - Component props.
 * @returns {React.ReactElement}
 */
export const SearchResultsSection = ({ results, linkingId, onLink }) => {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const hasResults = results.accounts.length
        || results.leads.length
        || results.contacts.length
        || results.opportunities.length;
    const handleLinkAccount = useCallback((account) => {
        onLink('Account', account);
    }, [onLink]);
    const handleLinkLead = useCallback((lead) => {
        onLink('Lead', lead);
    }, [onLink]);
    const handleLinkContact = useCallback((contact) => {
        onLink('Contact', contact);
    }, [onLink]);
    const handleLinkOpportunity = useCallback((opportunity) => {
        onLink('Opportunity', opportunity);
    }, [onLink]);
    if (!hasResults) {
        return (React.createElement("div", { className: classes.noResults }, t('dialog.searchResultsNotFound')));
    }
    return (React.createElement("div", { className: classes.section },
        !!results.accounts.length && (React.createElement(React.Fragment, null,
            React.createElement("h4", { className: classes.groupTitle }, t('record.type.account')),
            React.createElement("ul", { className: classes.list }, results.accounts.map(account => (React.createElement(RecordListItem, { actionLabelKey: 'dialog.link', actionType: BUTTON_TYPES.PRIMARY, disabled: linkingId !== null, icon: IconRecordAccount, isLoading: linkingId === account.accountId, key: account.accountId, name: account.accountName, onAction: handleLinkAccount, record: account })))))),
        !!results.leads.length && (React.createElement(React.Fragment, null,
            React.createElement("h4", { className: classes.groupTitle }, t('record.type.lead')),
            React.createElement("ul", { className: classes.list }, results.leads.map(lead => (React.createElement(RecordListItem, { actionLabelKey: 'dialog.link', actionType: BUTTON_TYPES.PRIMARY, disabled: linkingId !== null, icon: IconRecordLead, isLoading: linkingId === lead.leadId, key: lead.leadId, metadata: lead.leadCompany, name: lead.leadName, onAction: handleLinkLead, record: lead })))))),
        !!results.contacts.length && (React.createElement(React.Fragment, null,
            React.createElement("h4", { className: classes.groupTitle }, t('record.type.contact')),
            React.createElement("ul", { className: classes.list }, results.contacts.map(contact => (React.createElement(RecordListItem, { actionLabelKey: 'dialog.link', actionType: BUTTON_TYPES.PRIMARY, disabled: linkingId !== null, icon: IconRecordContact, isLoading: linkingId === contact.contactId, key: contact.contactId, metadata: contact.contactEmail, name: contact.contactName, onAction: handleLinkContact, record: contact })))))),
        !!results.opportunities.length && (React.createElement(React.Fragment, null,
            React.createElement("h4", { className: classes.groupTitle }, t('record.type.opportunity')),
            React.createElement("ul", { className: classes.list }, results.opportunities.map(opp => (React.createElement(RecordListItem, { actionLabelKey: 'dialog.link', actionType: BUTTON_TYPES.PRIMARY, disabled: linkingId !== null, icon: IconRecordOpportunity, isLoading: linkingId === opp.opportunityId, key: opp.opportunityId, metadata: `${opp.opportunityStage}${opp.amount !== undefined ? ` \u2022 $${opp.amount.toLocaleString()}` : ''}`, name: opp.opportunityName, onAction: handleLinkOpportunity, record: opp }))))))));
};
export default SearchResultsSection;
